import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import DataTableBase from "../interfaccia/DataTableBase";
import { BlockSkeleton } from "../interfaccia/loading";

const PER_PAGE_OPTIONS = [20, 50, 100, 200];

const defaultFilters = {
  search: "",
  vsArticolo: "",
  vsNome: "",
  vsVariante: "",
  vsCodProduttore: "",
  vsBrand: "",
  bsArticolo: "",
  bsVariante: "",
  bsBrand: "",
  motiviArtVar: "",
  motiviTaglia: "",
  motiviCreazione: "",
  ok: "",
  hasGiacenza: "",
};

const ADVANCED_FILTER_FIELDS = [
  ["search", "Ricerca libera"],
  ["vsArticolo", "VS Articolo"],
  ["vsNome", "VS Nome"],
  ["vsVariante", "VS Variante"],
  ["vsCodProduttore", "VS Cod produttore"],
  ["vsBrand", "VS Brand"],
  ["bsArticolo", "BS Articolo matchato"],
  ["bsVariante", "BS Variante matchata"],
  ["bsBrand", "BS Brand matchato"],
  ["motiviArtVar", "Motivo art-var (codice)"],
  ["motiviTaglia", "Motivo taglia (codice)"],
  ["motiviCreazione", "Motivo creazione (codice)"],
];

const normalizeSortDirection = (value) =>
  value === "desc" || value === "DESC" ? "desc" : "asc";
const MOTIVI_LABELS = {
  auto_art_var_ean: "Auto EAN (art-var)",
  auto_art_var_unique_producer: "Auto cod-prod univoco",
  auto_art_var_unique_prod_size: "Auto cod-prod+taglia univoci",
  auto_taglia_ean: "Auto EAN (taglia)",
  auto_taglia_matcha: "Auto taglia uguale",
  auto_taglia_convertita_exact: "Auto taglia convertita exact",
  auto_taglia_convertita_loose: "Auto taglia convertita loose",
  auto_taglia_unique_prod_size: "Auto taglia da cod-prod+taglia",
  auto_create_brand_not_in_vs: "Auto brand non presente in VS",
  auto_create_special_group7: "Auto gruppo7 speciale",
  auto_create_special_group1_58: "Auto campionario (58)",
  auto_create_special_group1_50: "Auto outlet (50)",
  auto_create_special_group1_49: "Auto contratto atleti Bottero (49)",
  auto_create_special_group1_44: "Auto contratto atleti (44)",
  auto_create_special_group1_38: "Auto riparazioni (38)",
  auto_create_special_group1_26: "Auto articoli regalo (26)",
  auto_create_special_group1_99: "Auto noleggio (99)",
  manual_set_match: "Manuale set match",
  manual_set_create: "Manuale set creazione",
  manual_set_taglia_da_aggiungere: "Manuale taglia da aggiungere",
  manual_set_articolo_da_rifare: "Manuale articolo da rifare",
};
const MOTIVI_FILTER_OPTIONS = {
  motiviArtVar: [
    "auto_art_var_ean",
    "auto_art_var_unique_producer",
    "auto_art_var_unique_prod_size",
    "manual_set_match",
  ],
  motiviTaglia: [
    "auto_taglia_ean",
    "auto_taglia_matcha",
    "auto_taglia_convertita_exact",
    "auto_taglia_convertita_loose",
    "auto_taglia_unique_prod_size",
    "manual_set_match",
    "manual_set_taglia_da_aggiungere",
    "manual_set_articolo_da_rifare",
  ],
  motiviCreazione: [
    "auto_create_brand_not_in_vs",
    "auto_create_special_group7",
    "auto_create_special_group1_58",
    "auto_create_special_group1_50",
    "auto_create_special_group1_49",
    "auto_create_special_group1_44",
    "auto_create_special_group1_38",
    "auto_create_special_group1_26",
    "auto_create_special_group1_99",
    "manual_set_create",
  ],
};
const formatMotiviSummary = (value) => {
  const parts = String(value || "")
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);
  if (!parts.length) return "";
  return parts.map((code) => MOTIVI_LABELS[code] || code).join(" | ");
};

function MatchBotteroVsSideTableSkeleton() {
  return (
    <div className="pa3 overflow-auto">
      <table className="collapse w-100 f7" style={{ minWidth: "1800px" }}>
        <thead>
          <tr>
            {[
              "80px",
              "200px",
              "130px",
              "180px",
              "160px",
              "130px",
              "120px",
              "140px",
              "110px",
              "110px",
              "170px",
              "110px",
            ].map((width, index) => (
              <th
                key={`vs-side-head-${index}`}
                className="pa2 bb bordoColAlt tl"
                style={{ width, minWidth: width }}
              >
                <BlockSkeleton tone="soft" width="78%" height={14} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }).map((_, rowIndex) => (
            <tr key={`vs-side-row-${rowIndex}`}>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width={24} height={24} />
              </td>
              {Array.from({ length: 11 }).map((__, cellIndex) => (
                <td key={`vs-side-cell-${rowIndex}-${cellIndex}`} className="pa2 bb bordoColAlt">
                  <BlockSkeleton
                    tone="soft"
                    width={cellIndex % 3 === 0 ? "72%" : cellIndex % 3 === 1 ? "88%" : "60%"}
                    height={20}
                    borderRadius={4}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchBotteroVsSidePage({ token }) {
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState("vsArticolo");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const loadRows = useCallback(async () => {
    if (!token) return;
    setLoadingRows(true);
    setError("");
    try {
      const params = {
        page,
        perPage,
        sortBy,
        sortDirection,
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim().length) {
          params[key] = value.trim();
        }
      });
      const response = await axios.get(
        `${apiUrl}/moda/match-bottero/list-vs-side`,
        {
          headers: authHeaders,
          params,
        },
      );
      const payload = response?.data ?? {};
      const list = Array.isArray(payload.rows) ? payload.rows : [];
      const mapped = list.map((row) => ({
        ...row,
        rowKey: `${row.codiceArticoloVs}-${row.numeroVarianteVs}`,
      }));
      setRows(mapped);
      const total = Number(payload?.pagination?.total ?? 0);
      setTotalRows(Number.isFinite(total) ? total : 0);
    } catch (err) {
      setError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento tabella",
      );
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoadingRows(false);
    }
  }, [authHeaders, filters, page, perPage, sortBy, sortDirection, token]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const updateDraftFilter = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setPage(1);
  };

  const resetFilters = () => {
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
    setPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: "Match",
        selector: (row) => Number(row.hasMatch) || 0,
        cell: (row) =>
          Number(row.hasMatch) === 1 ? (
            <span className="green b" style={{ fontSize: 18 }}>
              ✓
            </span>
          ) : (
            <span className="mid-gray b" style={{ fontSize: 16 }}>
              -
            </span>
          ),
        sortable: true,
        sortField: "ok",
        width: "80px",
        center: true,
      },
      {
        name: "VS Nome",
        selector: (row) => row.vsNome || "",
        cell: (row) => <span>{row.vsNome || ""}</span>,
        sortable: true,
        sortField: "vsNome",
        minWidth: "200px",
        wrap: true,
      },
      {
        name: "VS Articolo",
        selector: (row) => row.vsArticolo || "",
        cell: (row) => <span>{row.vsArticolo || ""}</span>,
        sortable: true,
        sortField: "vsArticolo",
        minWidth: "130px",
      },
      {
        name: "VS Variante",
        selector: (row) => row.vsVariante || "",
        cell: (row) => <span>{row.vsVariante || ""}</span>,
        sortable: true,
        sortField: "vsVariante",
        minWidth: "180px",
        wrap: true,
      },
      {
        name: "VS Cod produttore",
        selector: (row) => row.vsCodProduttore || "",
        cell: (row) => <span>{row.vsCodProduttore || ""}</span>,
        sortable: true,
        sortField: "vsCodProduttore",
        minWidth: "160px",
      },
      {
        name: "VS Cod modello",
        selector: (row) => row.vsCodModello || "",
        cell: (row) => <span>{row.vsCodModello || ""}</span>,
        sortable: true,
        sortField: "vsCodModello",
        minWidth: "130px",
      },
      {
        name: "VS Cod colore",
        selector: (row) => row.vsCodColore || "",
        cell: (row) => <span>{row.vsCodColore || ""}</span>,
        sortable: true,
        sortField: "vsCodColore",
        minWidth: "120px",
      },
      {
        name: "VS Brand",
        selector: (row) => row.vsBrand || "",
        cell: (row) => <span>{row.vsBrand || ""}</span>,
        sortable: true,
        sortField: "vsBrand",
        minWidth: "140px",
      },
      {
        name: "VS Stagione",
        selector: (row) => row.vsStagione || "",
        cell: (row) => <span>{row.vsStagione || ""}</span>,
        minWidth: "110px",
      },
      {
        name: "N° match BS",
        selector: (row) => Number(row.matchedRows) || 0,
        cell: (row) => (
          <span className="b">{Number(row.matchedRows || 0).toLocaleString("it-IT")}</span>
        ),
        minWidth: "110px",
      },
      {
        name: "N° match BS con taglia",
        selector: (row) => Number(row.matchedWithTagliaRows) || 0,
        cell: (row) => (
          <span>
            {Number(row.matchedWithTagliaRows || 0).toLocaleString("it-IT")}
          </span>
        ),
        minWidth: "170px",
      },
      {
        name: "Stock BS",
        selector: (row) => (Number(row.bsGiacenzaTotale) > 0 ? "SI" : "NO"),
        cell: (row) => (
          <span
            className={
              Number(row.bsGiacenzaTotale || 0) > 0 ? "dark-green b" : "mid-gray"
            }
          >
            {Number(row.bsGiacenzaTotale || 0) > 0 ? "SI" : "NO"}
          </span>
        ),
        minWidth: "110px",
      },
      {
        name: "BS Articoli matchati",
        selector: (row) => row.bsArticoli || "",
        cell: (row) => <span>{row.bsArticoli || "-"}</span>,
        sortable: true,
        sortField: "bsArticolo",
        minWidth: "200px",
        wrap: true,
      },
      {
        name: "BS Varianti matchate",
        selector: (row) => row.bsVarianti || "",
        cell: (row) => <span>{row.bsVarianti || "-"}</span>,
        sortable: true,
        sortField: "bsVariante",
        minWidth: "220px",
        wrap: true,
      },
      {
        name: "BS Brand",
        selector: (row) => row.bsBrand || "",
        cell: (row) => <span>{row.bsBrand || "-"}</span>,
        sortable: true,
        sortField: "bsBrand",
        minWidth: "180px",
        wrap: true,
      },
      {
        name: "BS Stagioni",
        selector: (row) => row.bsStagioni || "",
        cell: (row) => <span>{row.bsStagioni || "-"}</span>,
        sortable: true,
        sortField: "bsStagione",
        minWidth: "140px",
        wrap: true,
      },
      {
        name: "Motivo art-var",
        selector: (row) => formatMotiviSummary(row.motiviArtVar),
        cell: (row) => <span>{formatMotiviSummary(row.motiviArtVar) || "-"}</span>,
        minWidth: "220px",
        wrap: true,
      },
      {
        name: "Motivo taglia",
        selector: (row) => formatMotiviSummary(row.motiviTaglia),
        cell: (row) => <span>{formatMotiviSummary(row.motiviTaglia) || "-"}</span>,
        minWidth: "220px",
        wrap: true,
      },
      {
        name: "Motivo creazione",
        selector: (row) => formatMotiviSummary(row.motiviCreazione),
        cell: (row) => (
          <span>{formatMotiviSummary(row.motiviCreazione) || "-"}</span>
        ),
        minWidth: "240px",
        wrap: true,
      },
    ],
    [],
  );

  return (
    <div className="pa3">
      <div className="sfondoBiancoTema br3">
        <div className="pa3 sfondoColAltScuro mb3 flex justify-between items-center">
          <div className="f3 b">Art. VS matchati</div>
          <form
            className="flex flex-wrap items-end"
            onSubmit={(e) => {
              e.preventDefault();
              applyFilters();
            }}
          >
            <div className="mr3 mb2">
              <label className="f7 db mb1">VS Articolo</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.vsArticolo}
                onChange={(e) => updateDraftFilter("vsArticolo", e.target.value)}
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">VS Nome</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.vsNome}
                onChange={(e) => updateDraftFilter("vsNome", e.target.value)}
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Match</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.ok}
                onChange={(e) => updateDraftFilter("ok", e.target.value)}
              >
                <option value="">Tutti</option>
                <option value="1">Solo con match BS</option>
                <option value="0">Solo senza match BS</option>
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Giacenza BS</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.hasGiacenza}
                onChange={(e) =>
                  updateDraftFilter("hasGiacenza", e.target.value)
                }
              >
                <option value="">Tutte</option>
                <option value="1">Solo giacenza &gt; 0</option>
                <option value="0">Solo giacenza = 0</option>
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo art-var</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviArtVar}
                onChange={(e) =>
                  updateDraftFilter("motiviArtVar", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MOTIVI_FILTER_OPTIONS.motiviArtVar.map((code) => (
                  <option key={code} value={code}>
                    {MOTIVI_LABELS[code] || code}
                  </option>
                ))}
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo taglia</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviTaglia}
                onChange={(e) =>
                  updateDraftFilter("motiviTaglia", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MOTIVI_FILTER_OPTIONS.motiviTaglia.map((code) => (
                  <option key={code} value={code}>
                    {MOTIVI_LABELS[code] || code}
                  </option>
                ))}
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo creazione</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviCreazione}
                onChange={(e) =>
                  updateDraftFilter("motiviCreazione", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MOTIVI_FILTER_OPTIONS.motiviCreazione.map((code) => (
                  <option key={code} value={code}>
                    {MOTIVI_LABELS[code] || code}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 mr3"
            >
              Cerca
            </button>
            <button
              type="button"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
              onClick={() => setShowAdvancedFilters(true)}
            >
              Ricerca avanzata
            </button>
            <button
              type="button"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 ml2"
              onClick={resetFilters}
            >
              Reset filtri
            </button>
            <button
              type="button"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 ml2"
              onClick={loadRows}
            >
              Aggiorna
            </button>
          </form>
        </div>
        {error ? <div className="ph3 pb2 dark-red f6">{error}</div> : null}

        <div className="pa3 pt0">
          <DataTableBase
            columns={columns}
            data={rows}
            keyField="rowKey"
            progressPending={loadingRows}
            progressComponent={<MatchBotteroVsSideTableSkeleton />}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={PER_PAGE_OPTIONS}
            onChangePage={(newPage) => setPage(newPage)}
            onChangeRowsPerPage={(newPerPage, newPage) => {
              setPerPage(newPerPage);
              setPage(newPage);
            }}
            sortServer
            onSort={(column, direction) => {
              setSortBy(column?.sortField || "vsArticolo");
              setSortDirection(normalizeSortDirection(direction));
            }}
            noDataComponent="Nessun articolo VS trovato"
            dense
            highlightOnHover
            striped
          />
        </div>
      </div>

      {showAdvancedFilters ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5"
          style={{ background: "rgba(0, 0, 0, 0.35)" }}
          onClick={() => setShowAdvancedFilters(false)}
        >
          <div
            className="sfondoBiancoTema br3 pa3"
            style={{ width: "min(900px, 96vw)", margin: "8vh auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb3">
              <h3 className="ma0">Ricerca avanzata Art. VS matchati</h3>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                onClick={() => setShowAdvancedFilters(false)}
              >
                Chiudi
              </button>
            </div>
            <div className="flex flex-wrap">
              {ADVANCED_FILTER_FIELDS.map(([key, label]) => (
                <div className="w-50 ph2 mb3" key={key}>
                  <label className="f7 db mb1">{label}</label>
                  {Object.prototype.hasOwnProperty.call(
                    MOTIVI_FILTER_OPTIONS,
                    key,
                  ) ? (
                    <select
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters[key] || ""}
                      onChange={(e) => updateDraftFilter(key, e.target.value)}
                    >
                      <option value="">Tutti</option>
                      {MOTIVI_FILTER_OPTIONS[key].map((code) => (
                        <option key={code} value={code}>
                          {MOTIVI_LABELS[code] || code}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="input-reset ba bordoColAlt br2 pa2 w-100"
                      value={draftFilters[key] || ""}
                      onChange={(e) => updateDraftFilter(key, e.target.value)}
                    />
                  )}
                </div>
              ))}
              <div className="w-50 ph2 mb3">
                <label className="f7 db mb1">Giacenza BS</label>
                <select
                  className="input-reset ba bordoColAlt br2 pa2 w-100"
                  value={draftFilters.hasGiacenza}
                  onChange={(e) =>
                    updateDraftFilter("hasGiacenza", e.target.value)
                  }
                >
                  <option value="">Tutte</option>
                  <option value="1">Solo giacenza &gt; 0</option>
                  <option value="0">Solo giacenza = 0</option>
                </select>
              </div>
            </div>
            <div className="mt2">
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mr2"
                onClick={() => {
                  applyFilters();
                  setShowAdvancedFilters(false);
                }}
              >
                Applica filtri
              </button>
              <button
                type="button"
                className="pointer bn br2 ph3 pv2 bg-light-gray black"
                onClick={() => {
                  resetFilters();
                  setShowAdvancedFilters(false);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MatchBotteroVsSidePage;
